import { useCallback } from 'react';
import { OrgUserService } from '@api/OrgUserService';
import type { CreateOrgUserRequest, OrgUser } from '@api/OrgUserService';
import { invalidateOrgUserData } from '@stores/orgUserStore';

type InviteOrgUser = (
    request: CreateOrgUserRequest,
) => Promise<OrgUser | undefined>;

/*
 * Error semantics: dynoapeAPI's post swallows HTTP errors and resolves
 * undefined (the axios interceptor has already shown the error toast),
 * so the falsy guard below skips the invalidation on a failed invite;
 * only a missing org id throws.
 */
const useInviteOrgUser = (orgId?: string): { inviteOrgUser: InviteOrgUser } => {
    const inviteOrgUser = useCallback<InviteOrgUser>(
        async (request) => {
            if (!orgId) {
                throw new Error('useInviteOrgUser requires an org id');
            }

            const createdUser = await OrgUserService.createOrgUser(
                orgId,
                request,
            );

            if (!createdUser) {
                return undefined;
            }

            invalidateOrgUserData();

            return createdUser;
        },
        [orgId],
    );

    return { inviteOrgUser };
};

export default useInviteOrgUser;
